import { Immutable } from "immer";
import React, { useContext } from "react";
import { ProductSelectionGridSSR } from "tsi-common-react/src/apps/product-compare/ProductSelectionGridSSR/index";
import { RetailCardsSSR } from "tsi-common-react/src/apps/retail/RetailCardsSSR/index";
import { AccordionBlock } from "tsi-common-react/src/components/AccordionBlock/index";
import { AwardColumnsBlock } from "tsi-common-react/src/components/AwardColumnsBlock/index";
import { BannerBlock } from "tsi-common-react/src/components/BannerBlock/index";
import { ColumnsCarouselBlock } from "tsi-common-react/src/components/ColumnsCarouselBlock/index";
import { ComparisonBlock } from "tsi-common-react/src/components/ComparisonBlock/index";
import { CompatibilityAccordion } from "tsi-common-react/src/components/CompatibilityAccordion/index";
import { CustomerReviewsBlock } from "tsi-common-react/src/components/CustomerReviewsBlock/index";
import { EmailCaptureContent } from "tsi-common-react/src/components/EmailCaptureContent/index";
import { FAQsBlock } from "tsi-common-react/src/components/FAQsBlock/index";
import { FeaturedOffersCardBlock } from "tsi-common-react/src/components/FeaturedOffersCardBlock/index";
import { FeaturedProductLinesBlock } from "tsi-common-react/src/components/FeaturedProductLinesBlock/index";
import { FeaturedProductsCardBlock } from "tsi-common-react/src/components/FeaturedProductsCardBlock/index";
import { GalleryBlock } from "tsi-common-react/src/components/GalleryBlock/index";
import { PreApprovalBlock } from "tsi-common-react/src/components/PreApprovalBlock/index";
import { ProductCalloutBlock } from "tsi-common-react/src/components/ProductCalloutBlock/index";
import { PromoConditionalBlock } from "tsi-common-react/src/components/PromoConditionalBlock/index";
import { PromotionBlock } from "tsi-common-react/src/components/PromotionBlock/index";
import { RawHTMLBlock } from "tsi-common-react/src/components/RawHTMLBlock/index";
import { ReviewsFullBlock } from "tsi-common-react/src/components/ReviewsFullBlock/index";
import { TSIAlignedRichTextBlock } from "tsi-common-react/src/components/TSIAlignedRichTextBlock/index";
import { TabCardsBlock } from "tsi-common-react/src/components/TabCardsBlock/index";
import { ValuePropsBarBlock } from "tsi-common-react/src/components/ValuePropsBarBlock/index";
import { getGlobalSpacingClasses } from "tsi-common-react/src/components/_spacing/globalSpacing";
import { isoProductID } from "tsi-common-react/src/models/nominals";
import * as streamfieldModels from "tsi-common-react/src/models/streamfield-blocks";
import { check } from "tsi-common-react/src/models/utils";
import { getReactivatedContext } from "tsi-common-react/src/utils/environment";

import { Context, templates } from "@reactivated";
import type {
    CompatibilityAccordionModule as CompatibilityAccordionValue,
    EmailCaptureBlock as EmailCaptureBlockValue,
    GalleryBlock as GalleryBlockValue,
    SealyPromoConditionalBlock as PromoConditionalBlockValue,
    PromotionBlock as PromotionBlockValue,
    RawHTMLBlock as RawHTMLBlockValue,
    ReviewsFullBlock as ReviewsFullBlockValue,
    RichTextBlock as RichTextBlockValue,
    TabCardsBlock as TabCardsBlockValue,
    ValuePropBarBlock as ValuePropBarBlockValue,
} from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { AlignedHomepageHeroPromoBox } from "./AlignedHomepageHeroPromoBox";
import { HardcodedBlock } from "./HardcodedBlock";
import { SealyCompetitionCompareTableBlock } from "./SealyCompetitionCompareTableBlock";
import { SealyFinancingBanner } from "./SealyFinancingBanner";
import { SealyUGCGrid } from "./SealyUGCGrid";

/** Same static path the gallery-block SSR context resolves server-side. */
const GALLERY_DEFAULT_THUMBNAIL_PATH = "tsicommon_cms/img/video-thumbnail.jpg";

/**
 * Reads the reactivated ``STATIC_URL`` (FinancingCardImage precedent) so map
 * entries can rebuild URLs the legacy templates resolved with ``{% static %}``.
 */
const useStaticURL = (): string => {
    const context = useContext(Context) || getReactivatedContext();
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    return context?.STATIC_URL ?? "/static/";
};

/**
 * `gallery_block` -> shared gallery app component. The legacy
 * `gallery-block.html` passed three server-computed props
 * (`build_gallery_block_ssr_context`); the consolidated path rebuilds them
 * from the serialized block value:
 *
 *   - `blockData`: the serialized `GalleryBlock` itself, minus gallery entries
 *     whose SlideCollection is null (mirrors the SSR helper's filter).
 *   - `defaultThumbnail`: `static('tsicommon_cms/img/video-thumbnail.jpg')`,
 *     rebuilt from the reactivated `STATIC_URL` context.
 *   - `initialGalleryIndex`: the serialized `initial_gallery_index`, which
 *     the shared `GalleryBlock.get_data_for_client_render` computes
 *     server-side with the page-independent slice of the legacy
 *     `_get_initial_variant_id` priority chain: block-level `initial_variant`
 *     ProductChooser match (an editor can set it on ANY page; matching needs
 *     the variant's root-product pk + option values, which only the server
 *     can resolve from the serialized bare pk) -> default gallery (no
 *     product, no attributes) -> 0. Like the legacy SSR context, the index is
 *     computed over the UNfiltered galleries list while the data is filtered.
 *     Falls back to a client-computed default-gallery index if the field is
 *     ever absent. The page-level priority-1/-3 sources
 *     (`page.initial_variant_id` from the URL / page `initial_variant` FK)
 *     are PDP-only and don't exist on toolbox pages, so the PDP assembly
 *     (#33590) must NOT reuse this entry as-is -- it must override the index
 *     with variant matching from configurator state.
 *
 * Exported (with the optional `initialGalleryIndex` override) for the
 * ProductLineCollectionHeroGalleryTemplate (#33285): the PLC hero gallery is
 * the page's MAIN product gallery, so its index is computed server-side with
 * the full page-priority chain and threaded through the `gallery_ssr`
 * side-channel of `get_pdp_block_ssr`; when the override is absent (the plain
 * toolbox map entry -- StreamField never passes it), the serialized
 * page-independent index applies as before.
 */
export const GalleryBlockEntry = (block: {
    value: Immutable<GalleryBlockValue>;
    initialGalleryIndex?: number;
}): React.JSX.Element => {
    const staticURL = useStaticURL();
    const galleries = (block.value.galleries ?? []).filter(
        (g) => g.gallery !== null,
    );
    const defaultIdx = galleries.findIndex(
        (g) => !g.product && !(g.attributes ?? []).length,
    );
    const initialGalleryIndex =
        block.initialGalleryIndex ??
        block.value.initial_gallery_index ??
        (defaultIdx >= 0 ? defaultIdx : 0);
    return (
        <GalleryBlock
            blockData={{ ...block.value, galleries: galleries }}
            defaultThumbnail={`${staticURL}${GALLERY_DEFAULT_THUMBNAIL_PATH}`}
            initialGalleryIndex={initialGalleryIndex}
        />
    );
};

/**
 * Shared Toolbox block dispatcher map for every sealy page-body assembly
 * (#33585). Keys are the EXACT block-name strings from the Python
 * ``ToolboxContent`` / ``ToolboxContainers`` StreamBlocks
 * (``server/cms/assemblies/toolbox*.py``); consumers spread this map and layer
 * their own page-specific overrides on top.
 *
 * Built inside ``buildToolboxComponentsMap`` so the recursive
 * ``promo_conditional_block`` entry can reference the full map for its inner
 * ``content`` StreamBlock (which is itself a ``ToolboxContent``). Mirrors the
 * stearns/toppers ``_toolboxComponentsMap`` precedents.
 */
const buildToolboxComponentsMap = () => {
    const map = {
        // --- Simple {value} dispatch (component accepts `value` directly) ---
        accordion: AccordionBlock,
        aligned_rich_text_block: TSIAlignedRichTextBlock,
        award_columns_block: AwardColumnsBlock,
        banner_block: BannerBlock,
        // Sealy's ColumnsCarouselBlock subclass only narrows the theme choices
        // (``SealyColumnsCarouselBlock`` is assignable to the shared type), so
        // the shared component dispatches directly.
        columns_carousel_block: ColumnsCarouselBlock,
        comparison: ComparisonBlock,
        competition_compare_table_block: SealyCompetitionCompareTableBlock,
        customer_reviews: CustomerReviewsBlock,
        faqs_block: FAQsBlock,
        // GA4 events for both featured-card blocks arrive PRE-BUILT in the
        // serialized value (same tracker-tag builders the legacy templates
        // used), so plain {value} dispatch is sufficient (#33427 / #33420).
        featured_offers_card_block: FeaturedOffersCardBlock,
        // Sealy's FeaturedProductLinesBlock subclass only narrows the flair
        // choices (``SealyFeaturedProductLinesBlock`` is assignable to the
        // shared type), so the shared component dispatches directly.
        featured_product_lines_block: FeaturedProductLinesBlock,
        hardcoded_block: HardcodedBlock,
        homepage_hero_promo_box: AlignedHomepageHeroPromoBox,
        // `financing_block` -> SealyFinancingBanner gates itself on the
        // serialized `PreQualificationSettings.toolbox` flag
        // (`value.toolbox_enabled`), rendering nothing when toolbox pre-qual
        // is toggled off in Global Settings (stearns map precedent; sealy's
        // gating lives in the component, see its docstring).
        financing_block: SealyFinancingBanner,
        pre_approval_block: PreApprovalBlock,
        // Sealy's ProductCalloutBlock subclass swaps in the (narrower) sealy
        // overlay-banner sub-block; the shared component already renders
        // `overlay_banner`.
        product_callout: ProductCalloutBlock,
        // `reatured_products` is the (typo'd, load-bearing) sealy ToolboxContent
        // key for the shared FeaturedProductsCardBlock.
        reatured_products: FeaturedProductsCardBlock,
        ugc_grid: SealyUGCGrid,

        // `reviews_full_block` -> shared ReviewsFullBlock component
        // (#33428). The legacy `reviews-full-block.html` island props
        // (`{% react_product_reviews value.products value %}`: brand id from
        // the block's OWN products, deduped review aggregates, Reviews API
        // SSR prefill) arrive PRE-BUILT in the serialized `value.reviews`
        // (block-level `get_data_for_client_render`), so plain {value}
        // dispatch plus the sealy island flavor (`brand="Sealy"`, matching
        // this site's legacy ProductReviewsTemplate.tsx /
        // ReviewsFullBlockTemplate.tsx) is sufficient.
        reviews_full_block: (block: {
            value: Immutable<ReviewsFullBlockValue>;
        }) => <ReviewsFullBlock value={block.value} brand="Sealy" />,

        // `gallery_block` -> see GalleryBlockEntry docstring.
        gallery_block: GalleryBlockEntry,

        // `compatibility` reproduces the standalone
        // `compatibility-accordion-module.html`: the legacy
        // `{% if value.compatibility_panels %}` gate means an empty block
        // renders no island (and `CompatibilityAccordion` would otherwise
        // emit its wrapper divs even with zero panels), so render nothing at
        // all when the serialized panels array is empty. When populated, the
        // island is wrapped in the themed `<section>`. The wrapper MUST live
        // here, NOT in the shared component/template, because the legacy
        // per-block path already wraps it in the `.html` -- the consolidated
        // dispatcher bypasses the `.html`, so it owns the wrapper (stearns
        // precedent).
        compatibility: (block: {
            value: Immutable<CompatibilityAccordionValue>;
        }) => {
            if (!block.value.compatibility_panels.length) {
                return null;
            }
            return (
                <section className={block.value.background_style.style}>
                    <CompatibilityAccordion value={block.value} />
                </section>
            );
        },

        // `rich_text` -> thelabui RichTextBlock (raw rich-text string value).
        rich_text: (block: { value: Immutable<RichTextBlockValue> }) => (
            <RichTextBlock value={block.value} />
        ),

        // `HTML` (sealy's RawHTMLBlock key is capitalized) -> RawHTMLBlock.
        HTML: (block: { value: Immutable<RawHTMLBlockValue> }) => (
            <RawHTMLBlock value={block.value} />
        ),

        // `value_prop_bar` -> ValuePropsBarBlock (prop is `valuePropsData`).
        value_prop_bar: (block: {
            value: Immutable<ValuePropBarBlockValue>;
        }) => <ValuePropsBarBlock valuePropsData={block.value} />,

        // `promotion_block` -> NO blockIndex: the legacy include_block path
        // rendered every promotion through PromotionBlockTemplate with no
        // index, so the GA4 view_promotion creative_slot was ALWAYS
        // "promotion-block-1" regardless of position (adjudicated against the
        // dev templates during the #33578 review). The stearns/pillows/
        // toppers maps pass a positional index -- a cross-site divergence
        // flagged for the analytics owner -- but sealy reproduces legacy
        // exactly.
        promotion_block: (block: { value: Immutable<PromotionBlockValue> }) => (
            <PromotionBlock value={block.value} />
        ),

        // `email_capture` -> reproduces the shared email-capture-block.html:
        // maps the block's `email_snippet` into the EmailCaptureContent shape
        // and wraps it in the themed `<section>` (background_style +
        // responsive_visibility classes the standalone template adds). The
        // responsive_visibility class is the functional half -- it hides the
        // block per breakpoint, so dropping the wrapper could surface a block
        // the CMS configured hidden. Renders nothing when the snippet is
        // absent. (Stearns/toppers map precedent.)
        email_capture: (block: {
            value: Immutable<EmailCaptureBlockValue>;
        }) => {
            const snippet = block.value.email_snippet;
            if (!snippet) {
                return null;
            }
            return (
                <section
                    className={concatClassNames([
                        "email-capture-block",
                        block.value.background_style?.style ?? "",
                        block.value.responsive_visibility?.visibility ?? "",
                    ])}
                >
                    <EmailCaptureContent
                        content={{
                            email_snippet: {
                                title: snippet.title ?? "",
                                content: snippet.content,
                                confirm_content: snippet.confirm_content,
                                image: snippet.image,
                                footnotes: snippet.footnotes,
                            },
                        }}
                    />
                </section>
            );
        },

        // `tab_cards_block` -> shared TabCardsBlock container. Reproduces the
        // standalone `tab-cards-block.html`: the global-spacing `<section>`
        // wrapper (also the SSR store-read DOM boundary the legacy template
        // documented), the io-ts decode the legacy `cms_data` JSON went
        // through, and `initial_root_product_id` -- which the server computes
        // from `page.initial_variant*` and is therefore 0 on every (non-PDP)
        // toolbox page. The PDP assembly (#33590) must override this entry
        // with the real variant-derived root product id.
        tab_cards_block: (block: { value: Immutable<TabCardsBlockValue> }) => {
            const tabCards = check(
                streamfieldModels.TabCardsBlock.decode(block.value),
            );
            return (
                <section
                    className={getGlobalSpacingClasses(block.value.spacing)}
                >
                    <TabCardsBlock
                        tabCards={tabCards}
                        theme={tabCards.theme.theme || ""}
                        initialRootProductID={isoProductID.wrap(0)}
                    />
                </section>
            );
        },

        // --- SSR-envelope blocks (`block.value` carries the full server-
        // computed SSR props) ---

        // `product_selection_grid` -> ProductSelectionGridSSR. Plain spread of
        // the serialized SSR envelope, plus `showStartingAt` (the sealy
        // ProductSelectionGridBlockTemplate.tsx always passes it). NOTE
        // (stearns/toppers-parity gap, carried over deliberately): the
        // standalone `.html` wraps this in `<section
        // class="product-selection-grid-block {{theme}} {{visibility}}">`,
        // which the envelope serialization cannot rebuild (no
        // responsive_visibility in cms_data); fixing it for all three sites
        // means enriching the shared envelope serializer.
        product_selection_grid: (block: {
            value: Immutable<templates.ProductSelectionGridBlockTemplate> | null;
        }) =>
            block.value ? (
                <ProductSelectionGridSSR {...block.value} showStartingAt />
            ) : null,

        // `retail_cards` -> RetailCardsSSR. Empty serialized value; the SSR
        // container reads Redux/store-prefill (stearns precedent, and the
        // shared RetailCardsBlockTemplate re-export sealy already uses).
        retail_cards: () => <RetailCardsSSR />,

        // --- RECURSIVE: `promo_conditional_block` (ToolboxContainers) ---
        // Sealy's subclass (`SealyPromoConditionalBlock` ref) re-types
        // `content` as the sealy ToolboxContent, so it dispatches via this
        // same map. `map` is captured by reference below.
        promo_conditional_block: (block: {
            value: Immutable<PromoConditionalBlockValue>;
        }) => (
            <PromoConditionalBlock value={block.value}>
                <StreamField
                    tagName={React.Fragment}
                    value={block.value.content ?? []}
                    components={map}
                />
            </PromoConditionalBlock>
        ),
    } as const;
    return map;
};

export const toolboxComponentsMap = buildToolboxComponentsMap();
